<template>
  <b-container class="mt-10">
    <h5>{{ tableTitle }}</h5>

    <!-- Tab lọc theo trạng thái: Tất cả / Hợp lệ / Lỗi -->
    <b-nav
      v-if="showStatusTabs && items.length"
      class="my-3 font-weight-bold"
      tabs
      pills
    >
      <b-nav-item
        :active="activeTab === 'all'"
        @click="handleTabChange('all')"
      >
        Tất cả
        <b-badge variant="secondary" class="ml-2">{{ items.length }}</b-badge>
      </b-nav-item>
      <b-nav-item
        :active="activeTab === 'valid'"
        @click="handleTabChange('valid')"
      >
        Hợp lệ
        <b-badge variant="success" class="ml-2">{{ validCount }}</b-badge>
      </b-nav-item>
      <b-nav-item
        :active="activeTab === 'invalid'"
        @click="handleTabChange('invalid')"
      >
        Lỗi
        <b-badge variant="danger" class="ml-2">{{ invalidCount }}</b-badge>
      </b-nav-item>
    </b-nav>

    <!-- Empty state -->
    <div v-if="!items.length && !uploading" class="text-center py-5">
      <b-icon
        icon="file-earmark-excel"
        font-scale="3"
        variant="secondary"
      ></b-icon>
      <p class="mt-3 text-muted">{{ emptyMessage }}</p>
    </div>

    <!-- Table -->
    <b-table
      v-else
      class="myTable"
      responsive
      bordered
      hover
      :fields="fields"
      :items="displayedItems"
      id="my-table"
      :per-page="perPage"
      :current-page="currentPage"
      :busy="uploading"
      :show-empty="showStatusTabs"
      empty-text="Không có dòng nào ở trạng thái này."
    >
      <template v-slot:table-busy>
        <vcl-table :speed="5" :animate="true" :columns="10" />
      </template>
      <template v-slot:cell(count)="row">
        {{ (currentPage - 1) * perPage + row.index + 1 }}
      </template>
      <template #cell(status)="data">
        <b-badge :variant="data.item.isValid ? 'success' : 'danger'">
          {{ data.item.isValid ? 'Thành công' : 'Thất bại' }}
        </b-badge>
      </template>
      <template v-slot:cell(errors)="row">
        <div v-if="row.item.errors && row.item.errors.length > 0">
          <ul class="mb-0" style="font-size: 12px">
            <li
              class="list-unstyled"
              v-for="(error, index) in row.item.errors"
              :key="index"
            >
              - {{ error }}
            </li>
          </ul>
        </div>
        <span v-else style="color: green">Hợp lệ</span>
      </template>
      <!-- Pass through all custom cell slots from parent component -->
      <template
        v-for="(_, slotName) in customCellSlots"
        v-slot:[slotName]="scope"
      >
        <slot :name="slotName" v-bind="scope"></slot>
      </template>
    </b-table>
    <b-col v-if="!showStatusTabs" class="row mt-3">
      <b>
        Tổng số:
        {{ items.length }}
      </b>
      <span class="ml-3" v-if="validCount || validCount">
        (Thành công:
        <span class="text-success font-weight-bold">{{ validCount }}</span
        >, Thất bại:
        <span class="text-danger font-weight-bold">{{ invalidCount }}</span
        >)
      </span>
    </b-col>
    <b-pagination
      class="custom-pagination"
      v-show="displayedItems.length > perPage"
      :value="currentPage"
      @change="handlePageChange"
      :total-rows="displayedItems.length"
      :per-page="perPage"
      aria-controls="my-table"
      align="right"
    ></b-pagination>
  </b-container>
</template>

<script>
import { VclTable } from 'vue-content-loading';

export default {
  name: 'ExcelImportListTable',
  components: {
    VclTable,
  },
  props: {
    // Title của table
    tableTitle: {
      type: String,
      default: 'Danh sách từ file excel',
    },
    // Items để hiển thị trong table
    items: {
      type: Array,
      default: () => [],
    },
    // Uploading state
    uploading: {
      type: Boolean,
      default: false,
    },
    // Valid count
    validCount: {
      type: Number,
      default: 0,
    },
    // Invalid count
    invalidCount: {
      type: Number,
      default: 0,
    },
    // Current page
    currentPage: {
      type: Number,
      default: 1,
    },
    // Số items per page
    perPage: {
      type: Number,
      default: 10,
    },
    // Fields cho b-table (từ parent component)
    fields: {
      type: Array,
      required: true,
    },
    // Empty state message
    emptyMessage: {
      type: String,
      default: 'Chưa có dữ liệu. Vui lòng chọn file Excel để import.',
    },
    // Hiển thị tab lọc theo trạng thái (Tất cả / Hợp lệ / Lỗi)
    showStatusTabs: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Tab đang chọn khi bật showStatusTabs: 'all' | 'valid' | 'invalid'
      activeTab: 'all',
    };
  },
  computed: {
    // Items hiển thị sau khi lọc theo tab trạng thái
    displayedItems() {
      if (!this.showStatusTabs || this.activeTab === 'all') {
        return this.items;
      }
      if (this.activeTab === 'valid') {
        return this.items.filter((item) => item.isValid);
      }
      return this.items.filter((item) => !item.isValid);
    },
    customCellSlots() {
      const predefinedSlots = [
        'table-busy',
        'cell(count)',
        'cell(status)',
        'cell(errors)',
      ];
      const slots = {};
      if (this.$scopedSlots) {
        Object.keys(this.$scopedSlots).forEach((slotName) => {
          if (!predefinedSlots.includes(slotName)) {
            slots[slotName] = this.$scopedSlots[slotName];
          }
        });
      }
      return slots;
    },
  },
  watch: {
    // Nhập file mới → quay lại tab "Tất cả"
    items() {
      this.activeTab = 'all';
    },
  },
  methods: {
    handlePageChange(page) {
      this.$emit('update:currentPage', page);
    },
    handleTabChange(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      // Đổi tab → quay về trang đầu để tránh lệch số trang khi danh sách thu nhỏ
      this.$emit('update:currentPage', 1);
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  text-align: center;
}

.myTable /deep/ .table {
  color: #464e5f;
  background-color: transparent;
}
</style>
